/**
 * Phát audio TTS theo từng segment và cho UI biết đã đọc tới chữ nào.
 * Speech marks của Polly dùng millisecond. `end` là index ký tự (đã đổi từ byte ở API).
 */

export function revealUpTo(text, marks, timeMs) {
  const source = typeof text === 'string' ? text : '';
  const list = Array.isArray(marks) ? marks : [];
  const words = list.filter((mark) => (
    mark && mark.type === 'word' && typeof mark.time === 'number' && mark.time <= timeMs
  ));
  if (!words.length) {
    const hasWords = list.some((mark) => mark && mark.type === 'word');
    if (!hasWords && timeMs > 0) return source;
    return '';
  }
  const last = words[words.length - 1];
  const end = Math.max(0, Math.min(source.length, Number(last.end) || 0));
  return source.slice(0, end);
}

export function audioFromBase64(base64, contentType = 'audio/mpeg') {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: contentType || 'audio/mpeg' });
  const url = URL.createObjectURL(blob);
  return {
    audio: new Audio(url),
    revoke: () => URL.revokeObjectURL(url),
  };
}

function playClip({
  audio,
  text,
  marks,
  spoken,
  onReveal,
  isFirst,
  onPlaybackStart,
  isStopped,
  setCurrent,
  setFinish,
}) {
  return new Promise((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    setFinish(done);
    const reveal = () => {
      if (isStopped() || typeof onReveal !== 'function') return;
      const partial = revealUpTo(text, marks, audio.currentTime * 1000);
      const parts = spoken.slice();
      if (partial) parts.push(partial);
      onReveal(parts.join(' '));
    };
    audio.ontimeupdate = reveal;
    audio.onended = () => {
      spoken.push(text);
      if (!isStopped() && typeof onReveal === 'function') onReveal(spoken.join(' '));
      done();
    };
    audio.onerror = () => done();
    const markPlaybackStart = () => {
      if (isStopped() || !isFirst || typeof onPlaybackStart !== 'function') return;
      onPlaybackStart();
    };
    const played = audio.play();
    if (played && typeof played.then === 'function') {
      played.then(() => {
        markPlaybackStart();
        if (!settled) reveal();
      }).catch(done);
    } else {
      markPlaybackStart();
      if (!settled) reveal();
    }
    setCurrent(audio);
  });
}

export function createSpeechPlayer({ fetchSegment, createAudio = audioFromBase64 }) {
  let stopped = false;
  let current = null;
  let finishCurrent = () => {};

  return {
    stop() {
      stopped = true;
      if (current) current.pause();
      finishCurrent();
    },
    async start({ messageId, segments }, { onReveal, onPhase, onPlaybackStart }) {
      stopped = false;
      const spoken = [];
      const pending = new Map();
      const load = (index) => {
        if (!pending.has(index)) pending.set(index, fetchSegment(messageId, index));
        return pending.get(index);
      };

      if (typeof onPhase === 'function') onPhase('loading');

      for (let i = 0; i < segments.length; i += 1) {
        if (stopped) return;
        const segment = segments[i];
        const currentLoad = load(segment.index);
        if (segments[i + 1]) load(segments[i + 1].index);

        let data;
        try {
          data = await currentLoad;
        } catch (err) {
          if (!stopped && typeof onPhase === 'function') onPhase('error');
          return;
        }
        if (stopped) return;
        if (!data || !data.audio) {
          if (typeof onPhase === 'function') onPhase('error');
          return;
        }

        if (typeof onPhase === 'function') onPhase('playing');
        const created = createAudio(data.audio, data.contentType);
        const isFirstSegment = i === 0;
        await playClip({
          audio: created.audio,
          text: segment.text,
          marks: data.marks,
          spoken,
          onReveal,
          isFirst: isFirstSegment,
          onPlaybackStart,
          isStopped: () => stopped,
          setCurrent: (audio) => { current = audio; },
          setFinish: (finish) => { finishCurrent = finish; },
        });

        if (created.revoke) created.revoke();
        current = null;
        finishCurrent = () => {};
        if (stopped) return;
      }

      if (!stopped && typeof onPhase === 'function') onPhase('done');
    },
  };
}

// Hàng đợi phát khi audio từng câu tới trước khi Dify trả hết.
export function createSpeechQueue({ createAudio = audioFromBase64 } = {}) {
  let stopped = false;
  let closed = false;
  let current = null;
  let finishCurrent = () => {};
  let wake = () => {};
  const items = [];

  const poke = () => {
    const resume = wake;
    wake = () => {};
    resume();
  };

  return {
    push(item) {
      if (!item || !item.audio) return;
      items.push(item);
      poke();
    },
    close() {
      closed = true;
      poke();
    },
    stop() {
      stopped = true;
      closed = true;
      if (current) current.pause();
      finishCurrent();
      poke();
    },
    async start({ onReveal, onPhase, onPlaybackStart }) {
      stopped = false;
      const spoken = [];
      let index = 0;
      if (typeof onPhase === 'function') onPhase('loading');

      while (!stopped) {
        if (index >= items.length) {
          if (closed) break;
          await new Promise((resolve) => { wake = resolve; });
          continue;
        }
        const item = items[index];
        index += 1;
        if (typeof onPhase === 'function') onPhase('playing');
        const created = createAudio(item.audio, item.contentType);
        await playClip({
          audio: created.audio,
          text: item.text,
          marks: item.marks,
          spoken,
          onReveal,
          isFirst: index === 1,
          onPlaybackStart,
          isStopped: () => stopped,
          setCurrent: (audio) => { current = audio; },
          setFinish: (finish) => { finishCurrent = finish; },
        });
        if (created.revoke) created.revoke();
        current = null;
        finishCurrent = () => {};
      }

      if (!stopped && typeof onPhase === 'function') onPhase('done');
    },
  };
}
